import { useInfiniteQuery } from '@tanstack/react-query';
import axios from 'axios';
import { tpoGenUrls } from 'config/urls';
import { useMemo } from 'react';
import type { ApiPPG } from '../../../../types/promoMetrics';

interface RecommendedValuesPage {
    ppgs?: ApiPPG[];
    total_count?: number;
    applicable_metric_keys?: string[];
    default_visible_metric_keys?: string[];
}

interface RecommendedValuesBody {
    plan_id: string;
    event_id: string;
    attributes: Record<string, unknown>;
    start_date: string;
    end_date: string;
    cra_start_date: string;
    cra_end_date: string;
    ppg_ids: number[];
}

export interface UseRecommendedValuesArgs {
    planId: string;
    eventId: string;
    attributes: Record<string, unknown>;
    startDate: string;
    endDate: string;
    craStartDate: string;
    craEndDate: string;
    ppgIds: number[];
    enabled: boolean;
}

const QUERY_KEY = 'tpo-gen-recommended-values';
const PAGE_SIZE = 50;

const fetchRecommendedValues = async (body: RecommendedValuesBody, signal?: AbortSignal, offset?: number) => {
    const token = localStorage.getItem('accessToken');
    const response = await axios(tpoGenUrls.getRecommendedValues, {
        method: 'POST',
        headers: {
            Authorization: `Bearer ${token}`,
        },
        data: {
            ...body,
            limit: PAGE_SIZE,
            offset: offset || 0,
        },
        signal,
    });
    return response?.data;
};

const useRecommendedValues = ({
    planId,
    eventId,
    attributes,
    startDate,
    endDate,
    craStartDate,
    craEndDate,
    ppgIds,
    enabled,
}: UseRecommendedValuesArgs) => {
    const { data, isLoading, isFetching, isFetchingNextPage, isError, hasNextPage, fetchNextPage } = useInfiniteQuery({
        queryKey: [
            QUERY_KEY,
            planId,
            eventId,
            attributes,
            startDate,
            endDate,
            craStartDate,
            craEndDate,
            ppgIds,
        ],
        queryFn: ({ signal, pageParam }) =>
            fetchRecommendedValues(
                {
                    plan_id: planId,
                    event_id: eventId,
                    attributes,
                    start_date: startDate,
                    end_date: endDate,
                    cra_start_date: craStartDate,
                    cra_end_date: craEndDate,
                    ppg_ids: ppgIds,
                },
                signal,
                pageParam,
            ),
        initialPageParam: 0,
        getNextPageParam: (lastPage, _allPages, lastPageParam) => {
            return lastPage?.total_count > lastPageParam + PAGE_SIZE ? lastPageParam + PAGE_SIZE : undefined;
        },
        enabled,
    });

    const ppgData = useMemo<ApiPPG[]>(() => {
        if (!data?.pages) return [];
        return data.pages.flatMap((page: RecommendedValuesPage) => page.ppgs ?? []);
    }, [data]);

    // Server-computed visibility describes the whole request's cross-section, so it is
    // identical across pages — read it off the first page. `null` => backend omitted it.
    const { applicableMetricKeys, defaultVisibleMetricKeys } = useMemo(() => {
        const firstPage = data?.pages?.[0] as RecommendedValuesPage | undefined;
        return {
            applicableMetricKeys: firstPage?.applicable_metric_keys ?? null,
            defaultVisibleMetricKeys: firstPage?.default_visible_metric_keys ?? null,
        };
    }, [data]);

    return {
        ppgData,
        applicableMetricKeys,
        defaultVisibleMetricKeys,
        isLoading,
        isFetching,
        isFetchingNextPage,
        isError,
        hasNextPage,
        fetchNextPage,
    };
};

export default useRecommendedValues;
